import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    CcRequirementReviewMethod,
    CcRequirementSmartCompliance,
} from "server/models"
import {
    StoreType,
    RequirementFilters,
} from "client/store"
import {
    FormGroup,
    Label,
    Input,
    SelectDropdown,
    SelectDropdownItem,
    CheckboxWrapper,
    CheckButton,
    UserIcon,
    Empty,
    Button,
    SearchBar,
    SearchFilter,
} from "client/components"

interface SearchCcRequirementForm {
    filters: RequirementFilters
    onFilter: (filters: RequirementFilters) => void
}
interface SearchCcRequirement extends SearchCcRequirementForm {
    id: string
    leftOffset?: boolean
    rightOffset?: boolean
}

const CcRequirementSearch = ({
    id,
    leftOffset = false,
    rightOffset = false,
    filters,
    onFilter,
}: SearchCcRequirement) => {
    const typeList = useSelector((state: StoreType) => state.ccRequirementType.typeList)
    const priorityList = useSelector((state: StoreType) => state.ccRequirementPriority.priorityList)
    const statusList = useSelector((state: StoreType) => state.ccRequirementStatus.statusList)
    const methodList = useSelector((state: StoreType) => state.ccRequirementReviewMethod.methodList)
    const methodIds = useSelector((state: StoreType) => state.ccRequirementReviewMethod.methodIds)
    const complianceList = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.complianceList)
    const complianceIds = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.complianceIds)

    const userList = useSelector((state: StoreType) => state.user.userList)
    const lang = useSelector((state: StoreType) => state.system.lang)

    const extendedFilters: SearchFilter[] = []

    // extended filters
    if (filters.uniqueId && filters.uniqueId.length) {
        extendedFilters.push({
            title: `${lang.uniqueId}: ${filters.uniqueId}`,
            onClose: (obj: RequirementFilters) => ({
                ...obj,
                uniqueId: null,
            }),
        })
    }
    if (filters.name && filters.name.length) {
        extendedFilters.push({
            title: `${lang.name}: ${filters.name}`,
            onClose: (obj: RequirementFilters) => ({
                ...obj,
                name: null,
            }),
        })
    }
    if (filters.creator) {
        const user = userList[filters.creator]
        extendedFilters.push({
            title: `${lang.createdBy}: ${user.firstName} ${user.lastName}`,
            onClose: (obj: RequirementFilters) => ({
                ...obj,
                creator: null,
            }),
        })
    }
    if (filters.typeId) {
        const type = typeList[filters.typeId]
        extendedFilters.push({
            title: `${lang.type}: ${type ? type.name : ""}`,
            onClose: (obj: RequirementFilters) => ({
                ...obj,
                typeId: null,
            }),
        })
    }
    if (filters.priorityId) {
        const priority = priorityList[filters.priorityId]
        extendedFilters.push({
            title: `${lang.priority}: ${priority ? priority.name : ""}`,
            onClose: (obj: RequirementFilters) => ({
                ...obj,
                priorityId: null,
            }),
        })
    }
    if (filters.statusId) {
        const status = statusList[filters.statusId]
        extendedFilters.push({
            title: `${lang.status}: ${status ? status.name : ""}`,
            onClose: (obj: RequirementFilters) => ({
                ...obj,
                statusId: null,
            }),
        })
    }
    if (filters.reviewMethods) {
        const methodsArray: string[] = []
        methodIds.forEach((methodId) => {
            const method = methodList[methodId]
            if (method && filters.reviewMethods[methodId]) methodsArray.push(method.name)
        })
        if (methodsArray.length) {
            extendedFilters.push({
                title: `${lang.reviewMethods}: ${methodsArray.join(", ")}`,
                onClose: (obj: RequirementFilters) => ({
                    ...obj,
                    reviewMethods: null,
                }),
            })
        }
    }
    if (filters.smartCompliances) {
        const complianceArray: string[] = []
        complianceIds.forEach((complianceId) => {
            const compliance = complianceList[complianceId]
            if (compliance && filters.smartCompliances[complianceId]) complianceArray.push(compliance.name)
        })
        if (complianceArray.length) {
            extendedFilters.push({
                title: `${lang.smartCompliance}: ${complianceArray.join(", ")}`,
                onClose: (obj: RequirementFilters) => ({
                    ...obj,
                    smartCompliances: null,
                }),
            })
        }
    }

    return (
        <SearchBar
            id={id}
            filters={filters}
            leftOffset={leftOffset}
            rightOffset={rightOffset}
            extendedFilters={extendedFilters}
            filterForm={<CcRequirementFilterForm filters={filters} onFilter={onFilter} />}
            onFilter={onFilter}
        />
    )
}
export default CcRequirementSearch


const CcRequirementFilterForm = ({
    filters,
    onFilter,
}: SearchCcRequirementForm) => {
    const typeList = useSelector((state: StoreType) => state.ccRequirementType.typeList)
    const typeIds = useSelector((state: StoreType) => state.ccRequirementType.typeIds)
    const priorityList = useSelector((state: StoreType) => state.ccRequirementPriority.priorityList)
    const priorityIds = useSelector((state: StoreType) => state.ccRequirementPriority.priorityIds)
    const statusList = useSelector((state: StoreType) => state.ccRequirementStatus.statusList)
    const statusIds = useSelector((state: StoreType) => state.ccRequirementStatus.statusIds)
    const methodList = useSelector((state: StoreType) => state.ccRequirementReviewMethod.methodList)
    const methodIds = useSelector((state: StoreType) => state.ccRequirementReviewMethod.methodIds)
    const complianceList = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.complianceList)
    const complianceIds = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.complianceIds)

    const projectUserList = useSelector((state: StoreType) => state.projectUser.projectUserList)
    const project = useSelector((state: StoreType) => state.project.projectView)
    const userList = useSelector((state: StoreType) => state.user.userList)
    const lang = useSelector((state: StoreType) => state.system.lang)

    const [uniqueId, setUniqueId] = useState(filters.uniqueId || "")
    const [name, setName] = useState(filters.name || "")
    const [creator, setCreator] = useState(filters.creator)

    const [typeId, setTypeId] = useState(filters.typeId)
    const [priorityId, setPriorityId] = useState(filters.priorityId)
    const [statusId, setStatusId] = useState(filters.statusId)

    const [reviewMethods, setReviewMethods] = useState(filters.reviewMethods || {})
    const [smartCompliances, setSmartCompliances] = useState(filters.smartCompliances || {})

    useEffect(() => {
        setUniqueId(filters.uniqueId || "")
        setName(filters.name || "")
        setCreator(filters.creator || null)
        setTypeId(filters.typeId || null)
        setPriorityId(filters.priorityId || null)
        setStatusId(filters.statusId || null)
        setReviewMethods(filters.reviewMethods || {})
        setSmartCompliances(filters.smartCompliances || {})
    }, [filters])

    const onSearch = () => {
        const newFilters = {
            ...filters,
            uniqueId,
            name,
            creator,
            typeId,
            priorityId,
            statusId,
            reviewMethods,
            smartCompliances,
        }
        onFilter(newFilters)
    }

    const projectUsers = (projectUserList && projectUserList[project.id]) || []

    const columnNumber = 3

    const methodColumnLength = Math.ceil(methodIds.length / columnNumber)
    const methodColumns: { [columnIndex: number]: CcRequirementReviewMethod[] } = {}
    for (let i = 0; i < columnNumber; i++) {
        const column = []
        const start = i * methodColumnLength
        const end = start + methodColumnLength
        for (let k = start; k < end; k++) {
            const methodId = methodIds[k]
            column.push(methodList[methodId])
        }
        methodColumns[i] = column
    }

    const complianceColumnLength = Math.ceil(complianceIds.length / columnNumber)
    const complianceColumns: { [columnIndex: number]: CcRequirementSmartCompliance[] } = {}
    for (let i = 0; i < columnNumber; i++) {
        const column = []
        const start = i * complianceColumnLength
        const end = start + complianceColumnLength
        for (let k = start; k < end; k++) {
            const complianceId = complianceIds[k]
            column.push(complianceList[complianceId])
        }
        complianceColumns[i] = column
    }

    return (
        <Row>
            <Col xs={12}>
                <FormGroup>
                    <Label>{lang.uniqueId}</Label>
                    <Input type="text" value={uniqueId} onChange={(e) => setUniqueId(e.target.value)} />
                </FormGroup>
                <FormGroup>
                    <Label>{lang.name}</Label>
                    <Input type="text" value={name} onChange={(e) => setName(e.target.value)} />
                </FormGroup>

                <FormGroup>
                    <Label>{lang.createdBy}</Label>
                    <SelectDropdown title={
                        creator
                            ? <UserIcon user={userList[creator]} size="sm" />
                            : <Empty />
                    }
                    >
                        <SelectDropdownItem onClick={() => setCreator(null)}><Empty /></SelectDropdownItem>
                        {
                            projectUsers.map((id) => (
                                <SelectDropdownItem key={id} onClick={() => setCreator(id)}>
                                    <UserIcon user={userList[id]} size="sm" />
                                </SelectDropdownItem>
                            ))
                        }
                    </SelectDropdown>
                </FormGroup>

                <FormGroup>
                    <Label>{lang.type}</Label>
                    <SelectDropdown title={typeId && typeList[typeId] ? typeList[typeId].name : <Empty />}>
                        <SelectDropdownItem onClick={() => setTypeId(null)}><Empty /></SelectDropdownItem>
                        {
                            typeIds.map((id) => typeList[id] && (
                                <SelectDropdownItem key={id} onClick={() => setTypeId(id)}>
                                    {typeList[id].name}
                                </SelectDropdownItem>
                            ))
                        }
                    </SelectDropdown>
                </FormGroup>

                <FormGroup>
                    <Label>{lang.priority}</Label>
                    <SelectDropdown title={priorityId && priorityList[priorityId] ? priorityList[priorityId].name : <Empty />}>
                        <SelectDropdownItem onClick={() => setPriorityId(null)}><Empty /></SelectDropdownItem>
                        {
                            priorityIds.map((id) => priorityList[id] && (
                                <SelectDropdownItem key={id} onClick={() => setPriorityId(id)}>
                                    {priorityList[id].name}
                                </SelectDropdownItem>
                            ))
                        }
                    </SelectDropdown>
                </FormGroup>

                <FormGroup>
                    <Label>{lang.status}</Label>
                    <SelectDropdown title={statusId && statusList[statusId] ? statusList[statusId].name : <Empty />}>
                        <SelectDropdownItem onClick={() => setStatusId(null)}><Empty /></SelectDropdownItem>
                        {
                            statusIds.map((id) => statusList[id] && (
                                <SelectDropdownItem key={id} onClick={() => setStatusId(id)}>
                                    {statusList[id].name}
                                </SelectDropdownItem>
                            ))
                        }
                    </SelectDropdown>
                </FormGroup>

                <FormGroup>
                    <Label>{lang.reviewMethods}</Label>
                    <CheckboxWrapper>
                        {
                            Object.keys(methodColumns).map((index) => (
                                <Col key={index} xs={12} md={12 / columnNumber}>
                                    {
                                        methodColumns[Number(index)].map((method) => method && (
                                            <CheckButton
                                                key={method.id}
                                                title={method.name}
                                                checked={reviewMethods[method.id] === true}
                                                onChange={() => {
                                                    const newReviewMethods = { ...reviewMethods }
                                                    newReviewMethods[method.id] = !newReviewMethods[method.id]
                                                    setReviewMethods(newReviewMethods)
                                                }}
                                            />
                                        ))
                                    }
                                </Col>
                            ))
                        }
                    </CheckboxWrapper>
                </FormGroup>

                <FormGroup>
                    <Label>{lang.smartCompliances}</Label>
                    <CheckboxWrapper>
                        {
                            Object.keys(complianceColumns).map((index) => (
                                <Col key={index} xs={12} md={12 / columnNumber}>
                                    {
                                        complianceColumns[Number(index)].map((compliance) => compliance && (
                                            <CheckButton
                                                key={compliance.id}
                                                title={compliance.name}
                                                checked={smartCompliances[compliance.id]}
                                                onChange={() => {
                                                    const newSmartCompliances = { ...smartCompliances }
                                                    newSmartCompliances[compliance.id] = !newSmartCompliances[compliance.id]
                                                    setSmartCompliances(newSmartCompliances)
                                                }}
                                            />
                                        ))
                                    }
                                </Col>
                            ))
                        }
                    </CheckboxWrapper>
                </FormGroup>
            </Col>
            <Col xs={12}>
                <Button row onClick={onSearch}>{lang.search}</Button>
            </Col>
        </Row>
    )
}
